import { DYNAMIC_RANGE_MODE_DEF_BY_TYPE } from 'app-components/utils/date';
import { DateTime, Interval } from 'luxon';

import type { DateRangeType } from './types';

export const getDisplayedRangeForId = (
  ranges: DateRangeType[] | undefined,
  selectedRangeId: string | undefined,
): string => {
  const label = ranges?.find((item) => item.id === selectedRangeId)?.label;
  if (label) return label;
  switch (selectedRangeId) {
    case 'CUSTOM_SINCE':
    case 'CUSTOM_LAST':
    case 'CUSTOM_RECENT':
      return DYNAMIC_RANGE_MODE_DEF_BY_TYPE[selectedRangeId].label;
    default:
      return 'Custom';
  }
};

export const getDatesForId = (map: Map<string, DateRangeType>, id: string | undefined): (DateTime | null)[] | null => {
  if (typeof id === 'string') {
    const item = map.get(id);
    if (item?.getValue) {
      return item.getValue();
    }
    if (item?.value) {
      return [item.value.start, item.value.end];
    }
  }
  return null;
};

export const getRangeForId = (
  id: string | undefined,
  ranges: DateRangeType[] | undefined,
): DateRangeType | undefined => (id && ranges ? ranges.find((range) => range?.id === id) : undefined);

export const getIdForRange = (
  ranges: DateRangeType[],
  selectedStartDate: DateTime,
  selectedEndDate: DateTime,
): string | null => {
  const selectedInterval = Interval.fromDateTimes(selectedStartDate, selectedEndDate);

  const found = ranges.find((item) => {
    let shortcutInterval;
    if (item?.getValue) {
      const [start, end] = item.getValue();
      if (start && end) {
        shortcutInterval = Interval.fromDateTimes(start, end);
      }
    } else if (item?.value) {
      shortcutInterval = item.value;
    }

    return shortcutInterval && shortcutInterval.equals(selectedInterval);
  });

  return found?.id || null;
};

export type DateTimeOrStringType = DateTime | string | null | undefined;

export const toDateTime = (value: DateTimeOrStringType): DateTime | undefined => {
  if (!value) return undefined;
  if (DateTime.isDateTime(value)) return value.isValid ? value : undefined;
  if (typeof value === 'string') {
    const parsed = DateTime.fromISO(value);
    return parsed.isValid ? parsed : undefined;
  }
  return undefined;
};

export const formatDateRange = (startDate: DateTimeOrStringType, endDate: DateTimeOrStringType): string | undefined => {
  const start = toDateTime(startDate);
  const end = toDateTime(endDate);
  if (!start && !end) return undefined;

  const format = (date: DateTime | undefined): string => date?.toFormat('MM/dd/yyyy') ?? '';
  return `${format(start)} - ${format(end)}`;
};

export const getDateRangeDisplayLabel = (
  ranges: DateRangeType[] | undefined,
  startDate: DateTimeOrStringType,
  endDate: DateTimeOrStringType,
): string | undefined => {
  const start = toDateTime(startDate);
  const end = toDateTime(endDate);
  if (!start && !end) return undefined;

  if (start && end && ranges) {
    const selectedRangeId = getIdForRange(ranges, start, end);
    if (selectedRangeId) {
      return getDisplayedRangeForId(ranges, selectedRangeId);
    }
  }

  return formatDateRange(start, end);
};
